import { zodResolver } from "@hookform/resolvers/zod";
import { Button, TextField } from "@mui/material";
import { UserIcon, PersonIcon, XIcon } from "@phosphor-icons/react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { useTranslations } from "use-intl";
import { object } from "zod";

import CounterButton from "@/components/CounterButton/CounterButton";
import type { TRoom } from "@/features/hotels/types/types";
import {
  MAX_CHILDREN_PER_ROOM,
  MAX_VISITORS_PER_ROOM,
} from "@/features/hotels/utils/constants";
import {
  getRoomsValidationSchema,
  type HotelSearchFormValues,
} from "@/features/hotels/utils/hotelsValidation";

type Props = {
  value: HotelSearchFormValues["rooms"];
  onConfirm: (values: HotelSearchFormValues["rooms"]) => void;
};

export function VisitorsSelection({ value, onConfirm }: Props) {
  const t = useTranslations();

  const methods = useForm<Pick<HotelSearchFormValues, "rooms">>({
    defaultValues: {
      rooms: value,
    },
    resolver: zodResolver(
      object({
        rooms: getRoomsValidationSchema(t),
      }),
    ),
  });

  const { handleSubmit, setValue, control } = methods;

  const { fields, insert, remove, update } = useFieldArray({
    control,
    name: "rooms",
  });

  const newRoom: TRoom = {
    adults: 1,
    children: 0,
    childrenAges: [],
  };

  const rooms = useWatch({ control, name: "rooms" }) ?? [];

  return (
    <FormProvider {...methods}>
      <form
        noValidate
        onSubmit={(event) => {
          event.stopPropagation();

          handleSubmit((data) => {
            onConfirm(data.rooms);
          })(event);
        }}
      >
        <div className="flex h-full w-full flex-col">
          <div>
            {/* TODO: show this when enabling multiple rooms booking */}
            <div
              className="mb-3 flex items-center justify-between border-b border-stroke-neutral-soft py-2"
              style={{ display: "none" }}
            >
              <div className="flex">
                <h4 className="font-medium text-text-primary">
                  {t("hotels.search.rooms")}
                </h4>
              </div>

              <CounterButton
                value={rooms.length}
                minValue={1}
                maxValue={5}
                onChange={(value) =>
                  value < rooms.length ? remove(value) : insert(value, newRoom)
                }
              />
            </div>

            {fields.map((field, roomIndex) => {
              const room = rooms[roomIndex] ?? field; // fallback for first render

              const maxChildCount = Math.min(
                MAX_VISITORS_PER_ROOM - (room.adults ?? 1),
                MAX_CHILDREN_PER_ROOM,
              );
              const maxAdultCount =
                MAX_VISITORS_PER_ROOM - (room.children ?? 0);

              return (
                <div
                  key={field.id}
                  className="mb-3 w-full border-b border-stroke-neutral-soft"
                >
                  {/* TODO: show this when enabling multiple rooms booking */}
                  <div
                    className="flex w-full justify-center pb-4"
                    style={{ display: "none" }}
                  >
                    <h3 className="font-medium">
                      {t("hotels.search.room")} #{roomIndex + 1}
                    </h3>
                  </div>

                  <span className="mb-4 inline-block w-full text-xs text-text-tertiary">
                    {t("hotels.search.visitors")}
                  </span>

                  {/* Adults */}
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center">
                      <h4 className="font-medium text-text-primary">
                        {t("hotels.search.adult")}
                      </h4>
                      <UserIcon size={20} className="fill-secondary ml-4" />
                    </div>

                    <CounterButton
                      value={room.adults ?? 1}
                      onChange={(value) =>
                        update(roomIndex, {
                          ...room,
                          adults: value,
                        })
                      }
                      minValue={1}
                      maxValue={maxAdultCount}
                      minusButtonAriaLabel={t("hotels.search.removeAdult")}
                      plusButtonAriaLabel={t("hotels.search.addAdult")}
                    />
                  </div>

                  {/* Children */}
                  <div>
                    <div className="flex items-center justify-between py-2">
                      <div className="flex items-center">
                        <h4 className="font-medium text-text-primary">
                          {t("hotels.search.child")}
                        </h4>
                        <PersonIcon size={20} className="fill-secondary ml-4" />
                      </div>

                      <CounterButton
                        value={room.children ?? 0}
                        onChange={(value) => {
                          const nextAges = Array.from(
                            { length: value },
                            (_, i) => room.childrenAges?.[i],
                          );
                          update(roomIndex, {
                            ...room,
                            children: value,
                            childrenAges: nextAges,
                          });
                        }}
                        maxValue={maxChildCount}
                        minusButtonAriaLabel={t("hotels.search.removeChild")}
                        plusButtonAriaLabel={t("hotels.search.addChild")}
                      />
                    </div>

                    {/* Children ages */}
                    {room.children > 0 &&
                      Array.from({ length: room.children }).map(
                        (_, childIndex) => {
                          const name =
                            `rooms.${roomIndex}.childrenAges.${childIndex}` as const;
                          const currentAge = room.childrenAges?.[childIndex];

                          return (
                            <div
                              key={childIndex}
                              className="flex items-center justify-between py-2"
                            >
                              <h4 className="font-medium text-text-primary">
                                {t("hotels.search.child")} {childIndex + 1}
                              </h4>

                              <Controller
                                control={control}
                                name={name}
                                render={({ field, fieldState }) => (
                                  <TextField
                                    label={t("hotels.search.childsAge")}
                                    type="text"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    value={field.value ?? ""}
                                    onChange={(e) => {
                                      const raw = e.target.value;

                                      const numberValue = raw
                                        ? Number(raw)
                                        : undefined;

                                      const nextValue =
                                        !numberValue ||
                                        Number.isNaN(numberValue)
                                          ? raw
                                          : numberValue;

                                      field.onChange(nextValue);
                                    }}
                                    slotProps={{
                                      htmlInput: {
                                        inputMode: "numeric",
                                        pattern: "d*",
                                      },
                                      input: {
                                        endAdornment: currentAge ? (
                                          <button
                                            className="shrink-0"
                                            type="button"
                                            onClick={() => {
                                              setValue(name, "", {
                                                shouldDirty: true,
                                                shouldTouch: true,
                                                shouldValidate: true,
                                              });
                                            }}
                                          >
                                            <XIcon
                                              size={20}
                                              className="fill-text-placeholder"
                                            />
                                          </button>
                                        ) : null,
                                      },
                                    }}
                                  />
                                )}
                              />
                            </div>
                          );
                        },
                      )}
                  </div>
                </div>
              );
            })}
          </div>

          <Button fullWidth type="submit" variant="contained">
            {t("confirm")}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
